import { ROUTE_CHANGE_EVENT } from '../router';

const SITE_URL = 'https://alejandrosn.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo_alejandrosn.png`;
const SITE_NAME = 'AlejandroSN';

export interface SeoMeta {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  /** Por defecto `website` en `/` y `article` en el resto. */
  ogType?: 'website' | 'article';
  noindex?: boolean;
}

const ROUTE_SEO: SeoMeta[] = [
  {
    path: '/',
    title: 'AlejandroSN | Desarrollo web, Cloudflare y automatización',
    description:
      'Alejandro Serrano: desarrollo web, automatización e integraciones con JavaScript y Cloudflare. Herramientas digitales completas, de la idea a producción.',
  },
  {
    path: '/sobre-mi',
    title: 'Sobre mí | Alejandro Serrano, desarrollador web — AlejandroSN',
    description:
      'Alejandro Serrano Notivoli, desarrollador de JavaScript, Cloudflare y automatización. Productos completos, de la idea al despliegue. Reserva una llamada.',
  },
  {
    path: '/agencias',
    title: 'Partner técnico para agencias | Freelance — AlejandroSN',
    description:
      'Desarrollador freelance para agencias: integraciones, automatización y webs a medida. Tú mantienes al cliente; yo ejecuto el encargo. Sin contacto con su cliente sin autorización.',
  },
  {
    path: '/blunticle',
    title: 'Blunticle | Producto de AlejandroSN',
    description:
      'Blunticle es la herramienta de AlejandroSN que convierte una idea en contenido con un equipo de agentes de IA: Director, Writers, Secretario y Artista.',
  },
  {
    path: '/subbyswap',
    title: 'SubbySwap | Producto de AlejandroSN',
    description:
      'SubbySwap es el árbitro automático de intercambios de recomendaciones entre creadores de Substack: matchmaking por ligas, contratos claros y verificación diaria.',
  },
  {
    path: '/traviapp',
    title: 'Traviapp | Producto de AlejandroSN',
    description:
      'Traviapp es el link-in-bio de AlejandroSN: perfil, enlaces, secciones, suscriptores y analítica en una página que se siente como tuya.',
  },
  {
    path: '/tag-nfc',
    title: 'NFC Pop | Producto de AlejandroSN',
    description:
      'NFC Pop es un soporte físico para comercios con NFC y QR. El establecimiento cambia la URL cuando quiere: ofertas, reseñas o redes, sin reimprimir.',
  },
  {
    path: '/blunticle/agentes/director',
    title: 'El Director — Blunticle | AlejandroSN',
    description:
      'El Director de Blunticle analiza tu producto, define estrategia de contenido, keywords y tono antes de que el equipo escriba.',
  },
  {
    path: '/blunticle/agentes/writers',
    title: 'Los Writers — Blunticle | AlejandroSN',
    description:
      'Los Writers de Blunticle redactan artículos de blog y posts para redes con SEO y GEO integrados.',
  },
  {
    path: '/blunticle/agentes/secretario',
    title: 'El Secretario — Blunticle | AlejandroSN',
    description:
      'El Secretario de Blunticle audita el contenido, corrige y garantiza coherencia antes de la entrega.',
  },
  {
    path: '/blunticle/agentes/artista',
    title: 'El Artista — Blunticle | AlejandroSN',
    description:
      'El Artista de Blunticle genera imágenes listas para publicar a partir del brief del Director.',
  },
  {
    path: '/blog',
    title: 'Blog | AlejandroSN',
    description:
      'Artículos de Alejandro Serrano sobre desarrollo web, Cloudflare, automatización y producto digital.',
  },
  {
    path: '/servicios',
    title: 'Desarrollo web y automatización para negocios | AlejandroSN',
    description:
      'Webs, tiendas online, automatizaciones, paneles e integraciones para negocios. De la idea a producción con Alejandro Serrano. Llama, reserva o escribe.',
    ogType: 'website',
  },
  {
    path: '/contacto',
    title: 'Contacto | AlejandroSN',
    description:
      'Contacta con Alejandro Serrano para desarrollo web, automatización o integraciones. Reserva una llamada o escribe a soporte@alejandrosn.com.',
  },
  {
    path: '/politica-privacidad',
    title: 'Política de Privacidad | AlejandroSN',
    description: 'Política de privacidad del sitio web de AlejandroSN.',
    noindex: true,
  },
  {
    path: '/aviso-legal',
    title: 'Aviso Legal | AlejandroSN',
    description: 'Aviso legal del sitio web de AlejandroSN.',
    noindex: true,
  },
  {
    path: '/politica-cookies',
    title: 'Política de Cookies | AlejandroSN',
    description: 'Política de cookies del sitio web de AlejandroSN.',
    noindex: true,
  },
];

const DEFAULT_SEO: SeoMeta = ROUTE_SEO[0];

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function resolveSeo(pathname: string): SeoMeta {
  const exact = ROUTE_SEO.find((entry) => entry.path === pathname);
  if (exact) return exact;
  return {
    ...DEFAULT_SEO,
    path: pathname,
  };
}

export function applySeo(pathname = location.pathname): void {
  const seo = resolveSeo(pathname);
  const url = `${SITE_URL}${seo.path === '/' ? '/' : seo.path}`;
  const image = seo.ogImage ?? DEFAULT_OG_IMAGE;

  document.title = seo.title;

  upsertMeta('name', 'description', seo.description);
  upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow');
  upsertMeta('name', 'author', 'Alejandro Serrano Notivoli');

  upsertMeta('property', 'og:type', seo.ogType ?? (seo.path === '/' ? 'website' : 'article'));
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:locale', 'es_ES');
  upsertMeta('property', 'og:title', seo.title);
  upsertMeta('property', 'og:description', seo.description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', image);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', seo.title);
  upsertMeta('name', 'twitter:description', seo.description);
  upsertMeta('name', 'twitter:image', image);

  upsertLink('canonical', url);
}

export function setupSeoListener(): void {
  applySeo(location.pathname);

  window.addEventListener(ROUTE_CHANGE_EVENT, ((event: CustomEvent<{ pathname: string }>) => {
    applySeo(event.detail.pathname);
  }) as EventListener);
}
